import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, User, Phone, Mail, MapPin, FileText, DollarSign, Briefcase, Droplet, Zap, Thermometer, Wrench, Sparkles, Star, Users, TrendingUp, Car, Home, Bug, Hammer, Trees, Truck, DoorOpen, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  { slug: "plumbing", name: "Plumbing", icon: Droplet },
  { slug: "electrical", name: "Electrical", icon: Zap },
  { slug: "hvac", name: "HVAC", icon: Thermometer },
  { slug: "handyman", name: "Handyman", icon: Wrench },
  { slug: "cleaning", name: "Cleaning", icon: Sparkles },
  { slug: "mechanics", name: "Auto Mechanics", icon: Car },
  { slug: "roofing", name: "Roofing", icon: Home },
  { slug: "pest-control", name: "Pest Control", icon: Bug },
  { slug: "remodeling", name: "Remodeling", icon: Hammer },
  { slug: "landscaping", name: "Landscaping", icon: Trees },
  { slug: "moving", name: "Moving", icon: Truck },
  { slug: "garage-door", name: "Garage Door", icon: DoorOpen },
  { slug: "appliance-repair", name: "Appliance Repair", icon: Settings },
];

const JoinAsProPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    services: [],
    experience_years: "",
    hourly_rate: "",
    location: "",
    description: "",
    referral_code: new URLSearchParams(window.location.search).get('ref') || ""
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceToggle = (slug) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(slug)
        ? prev.services.filter(s => s !== slug)
        : [...prev.services, slug]
    }));
  };

  const validateForm = () => {
    return (
      formData.name &&
      formData.email &&
      formData.phone &&
      formData.services.length > 0 &&
      formData.experience_years &&
      formData.hourly_rate &&
      formData.location &&
      formData.description
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/workers`, {
        ...formData,
        experience_years: parseInt(formData.experience_years),
        hourly_rate: parseFloat(formData.hourly_rate)
      });
      setSubmitted(true);
      toast.success("Registration successful! Welcome to the team.");
    } catch (error) {
      console.error("Error registering:", error);
      if (error.response?.data?.detail === "Email already registered") {
        toast.error("This email is already registered.");
      } else {
        toast.error("Failed to register. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="join-success-page">
        <Navbar />
        <div className="container-custom py-20">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-[#e6f4ea] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#1e8e3e]" />
            </div>
            <h1 className="text-3xl font-medium text-[#202124] mb-4">
              {t("joinAsPro.successTitle")}
            </h1>
            <p className="text-[#5f6368] mb-8">
              {t("joinAsPro.successMessage")} {formData.services.join(", ")} {t("joinAsPro.services")}.
            </p>

            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate("/browse")}
                className="rounded-full px-6 border-[#dadce0]"
                data-testid="view-profile-btn"
              >
                {t("common.viewAll")}
              </Button>
              <Button
                onClick={() => navigate("/")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-6"
                data-testid="back-home-btn"
              >
                {t("common.backToHome")}
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="join-as-pro-page">
      <Navbar />

      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-[#e8f0fe] to-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-sm font-medium text-[#1a73e8] mb-4 uppercase tracking-wider">
                {t("joinAsPro.title")}
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-[#202124] mb-6">
                {t("joinAsPro.headline")}
              </h1>
              <p className="text-lg text-[#5f6368] mb-8">
                {t("joinAsPro.subtitle")}
              </p>
              
              <div className="grid grid-cols-3 gap-6">
                {[
                  { icon: Users, value: "500+", labelKey: "stats.totalPros" },
                  { icon: Star, value: "4.8", labelKey: "stats.avgRating" },
                  { icon: TrendingUp, value: "95%", label: "Match Rate" }
                ].map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-2">
                      <stat.icon className="w-5 h-5 text-[#1a73e8]" />
                    </div>
                    <div className="text-xl font-medium text-[#202124]">{stat.value}</div>
                    <div className="text-sm text-[#5f6368]">{stat.labelKey ? t(stat.labelKey) : stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden md:block">
              <img
                src="https://images.pexels.com/photos/8486978/pexels-photo-8486978.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional worker"
                className="rounded-3xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section-spacing">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="form-container" data-testid="registration-form">
              <h2 className="text-2xl font-medium text-[#202124] mb-2">
                {t("joinAsPro.createProfile")}
              </h2>
              <p className="text-[#5f6368] mb-8">
                {t("joinAsPro.formSubtitle")}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Info */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-[#202124] mb-2 block">
                      <User className="w-4 h-4 inline mr-2" />
                      {t("forms.name")} *
                    </Label>
                    <Input
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                      data-testid="name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#202124] mb-2 block">
                      <Mail className="w-4 h-4 inline mr-2" />
                      {t("forms.email")} *
                    </Label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-[#202124] mb-2 block">
                      <Phone className="w-4 h-4 inline mr-2" />
                      {t("forms.phone")} *
                    </Label>
                    <Input
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#202124] mb-2 block">
                      <MapPin className="w-4 h-4 inline mr-2" />
                      {t("forms.location")} *
                    </Label>
                    <Input
                      placeholder="City, State"
                      value={formData.location}
                      onChange={(e) => handleInputChange("location", e.target.value)}
                      className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                      data-testid="location-input"
                    />
                  </div>
                </div>

                {/* Services */}
                <div>
                  <Label className="text-sm font-medium text-[#202124] mb-3 block">
                    {t("forms.servicesOffered")} *
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {serviceOptions.map((service) => {
                      const isSelected = formData.services.includes(service.slug);
                      return (
                        <button
                          key={service.slug}
                          type="button"
                          onClick={() => handleServiceToggle(service.slug)}
                          className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                            isSelected
                              ? "border-[#1a73e8] bg-[#e8f0fe]"
                              : "border-[#dadce0] hover:border-[#1a73e8]/50"
                          }`}
                          data-testid={`service-checkbox-${service.slug}`}
                        >
                          <service.icon className={`w-6 h-6 ${isSelected ? "text-[#1a73e8]" : "text-[#5f6368]"}`} />
                          <span className={`text-sm font-medium ${isSelected ? "text-[#1a73e8]" : "text-[#202124]"}`}>
                            {service.name}
                          </span>
                          {isSelected && (
                            <CheckCircle className="w-4 h-4 text-[#1a73e8] absolute top-2 right-2" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Experience & Rate */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-[#202124] mb-2 block">
                      <Briefcase className="w-4 h-4 inline mr-2" />
                      {t("forms.experience")} *
                    </Label>
                    <Input
                      type="number"
                      placeholder="5"
                      min="0"
                      value={formData.experience_years}
                      onChange={(e) => handleInputChange("experience_years", e.target.value)}
                      className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                      data-testid="experience-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#202124] mb-2 block">
                      <DollarSign className="w-4 h-4 inline mr-2" />
                      {t("forms.hourlyRate")} *
                    </Label>
                    <Input
                      type="number"
                      placeholder="50"
                      min="0"
                      value={formData.hourly_rate}
                      onChange={(e) => handleInputChange("hourly_rate", e.target.value)}
                      className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                      data-testid="rate-input"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label className="text-sm font-medium text-[#202124] mb-2 block">
                    <FileText className="w-4 h-4 inline mr-2" />
                    {t("forms.aboutYou")} *
                  </Label>
                  <Textarea
                    placeholder={t("forms.aboutPlaceholder")}
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    className="min-h-[120px] rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                    data-testid="description-input"
                  />
                </div>

                {/* Referral Code */}
                <div>
                  <Label className="text-sm font-medium text-[#202124] mb-2 block">
                    {t("forms.referralCode")}
                  </Label>
                  <Input
                    placeholder={t("forms.referralPlaceholder")}
                    value={formData.referral_code}
                    onChange={(e) => handleInputChange("referral_code", e.target.value)}
                    className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                    data-testid="referral-code-input"
                  />
                  <p className="text-xs text-[#5f6368] mt-1">
                    {t("forms.referralHint")}
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={!validateForm() || loading}
                  className="w-full bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full py-3 h-auto text-base font-medium"
                  data-testid="submit-registration-btn"
                >
                  {loading ? t("common.creating") : t("joinAsPro.createProfile")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <p className="text-xs text-[#5f6368] text-center">
                  {t("forms.termsAgreement")}
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#f8f9fa]">
        <div className="container-custom">
          <h2 className="text-2xl font-medium text-[#202124] text-center mb-12">
            {t("joinAsPro.whyJoin")}
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { titleKey: "joinAsPro.benefit1Title", descKey: "joinAsPro.benefit1Desc" },
              { titleKey: "joinAsPro.benefit2Title", descKey: "joinAsPro.benefit2Desc" },
              { titleKey: "joinAsPro.benefit3Title", descKey: "joinAsPro.benefit3Desc" },
              { titleKey: "joinAsPro.benefit4Title", descKey: "joinAsPro.benefit4Desc" }
            ].map((benefit, idx) => (
              <div key={idx} className="text-center p-6 bg-white rounded-2xl">
                <div className="w-10 h-10 rounded-full bg-[#e6f4ea] flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-5 h-5 text-[#1e8e3e]" />
                </div>
                <h3 className="font-medium text-[#202124] mb-2">{t(benefit.titleKey)}</h3>
                <p className="text-sm text-[#5f6368]">{t(benefit.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JoinAsProPage;
